<template>
  <v-card>
    <v-card-title>
      <v-icon left>mdi-controller</v-icon>
      Controles de Sesión
    </v-card-title>
    
    <v-card-text>
      <!-- Session status message -->
      <v-alert
        :type="statusAlert.type"
        variant="tonal"
        class="mb-4"
      >
        <v-icon left>{{ statusAlert.icon }}</v-icon>
        {{ statusAlert.message }}
      </v-alert>

      <!-- Control buttons based on session status -->
      <div class="d-flex flex-column ga-3">
        <!-- Start Session Button -->
        <v-btn
          v-if="sessionStatus === 'waiting'"
          color="success"
          size="large"
          :loading="loading"
          :disabled="!canStartQuiz"
          @click="confirmStartSession"
          block
        >
          <v-icon left>mdi-play</v-icon>
          Iniciar Quiz
          <template #append>
            <v-chip
              color="white"
              size="small"
              class="ml-2"
            >
              {{ waitingCount }}
            </v-chip>
          </template>
        </v-btn>

        <!-- Complete Session Button -->
        <v-btn
          v-if="sessionStatus === 'active'"
          color="primary"
          size="large"
          :loading="loading"
          @click="confirmCompleteSession"
          block
        >
          <v-icon left>mdi-stop</v-icon>
          Finalizar Quiz
        </v-btn>

        <!-- Session Completed State -->
        <div v-if="sessionStatus === 'completed'" class="text-center py-4">
          <v-icon size="48" color="success" class="mb-2">mdi-check-circle</v-icon>
          <div class="text-h6">Sesión Completada</div>
          <div class="text-body-2 text-medium-emphasis">
            El quiz ha sido finalizado exitosamente
          </div>
        </div>

        <!-- Additional actions for active sessions -->
        <div v-if="sessionStatus === 'active'" class="d-flex ga-2">
          <v-btn
            variant="outlined"
            color="info"
            :loading="loading"
            @click="refreshSession"
            flex
          >
            <v-icon left>mdi-refresh</v-icon>
            Actualizar
          </v-btn>
          
          <v-btn
            variant="outlined"
            color="warning"
            @click="showSessionInfo = true"
            flex
          >
            <v-icon left>mdi-information</v-icon>
            Info
          </v-btn>
        </div>
      </div>

      <!-- Help text for waiting state -->
      <div v-if="sessionStatus === 'waiting'" class="mt-4">
        <div class="text-caption text-medium-emphasis">
          <v-icon size="small" class="mr-1">mdi-lightbulb-outline</v-icon>
          {{ canStartQuiz ? 'Haz clic en "Iniciar Quiz" cuando estés listo.' : 'Espera a que al menos un estudiante se una para poder iniciar.' }}
        </div>
      </div>
    </v-card-text>

    <!-- Start session confirmation dialog -->
    <v-dialog v-model="showStartDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon left color="success">mdi-play</v-icon>
          Iniciar Quiz
        </v-card-title>
        <v-card-text>
          ¿Estás seguro de que quieres iniciar el quiz?
          <br><br>
          <strong>{{ waitingCount }}</strong> estudiante(s) comenzarán inmediatamente.
          <br><br>
          <div class="text-caption text-medium-emphasis">
            Una vez iniciado, no se pueden agregar más estudiantes.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey"
            variant="text"
            @click="showStartDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="success"
            variant="tonal"
            :loading="loading"
            @click="startSession"
          >
            <v-icon left>mdi-play</v-icon>
            Iniciar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Complete session confirmation dialog -->
    <v-dialog v-model="showCompleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon left color="primary">mdi-stop</v-icon>
          Finalizar Quiz
        </v-card-title>
        <v-card-text>
          ¿Estás seguro de que quieres finalizar el quiz?
          <br><br>
          Todos los estudiantes serán notificados y no podrán continuar respondiendo.
          <br><br>
          <div class="text-caption text-medium-emphasis">
            Esta acción no se puede deshacer.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey"
            variant="text"
            @click="showCompleteDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            variant="tonal"
            :loading="loading"
            @click="completeSession"
          >
            <v-icon left>mdi-stop</v-icon>
            Finalizar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Session info dialog -->
    <v-dialog v-model="showSessionInfo" max-width="500">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="info">mdi-information</v-icon>
          Información de la Sesión
        </v-card-title>
        <v-card-text>
          <div class="d-flex flex-column ga-3">
            <div>
              <div class="text-subtitle-2 text-medium-emphasis">Código PIN</div>
              <v-chip color="primary" class="font-monospace">{{ pinCode }}</v-chip>
            </div>
            <div>
              <div class="text-subtitle-2 text-medium-emphasis">ID de Sesión</div>
              <div class="font-monospace text-body-2">{{ sessionId }}</div>
            </div>
            <div>
              <div class="text-subtitle-2 text-medium-emphasis">Estado</div>
              <v-chip :color="statusAlert.color" size="small">{{ statusAlert.status }}</v-chip>
            </div>
            <div>
              <div class="text-subtitle-2 text-medium-emphasis">Participantes Totales</div>
              <div class="text-h6">{{ participants.length }}</div>
            </div>
            <div>
              <div class="text-subtitle-2 text-medium-emphasis">Conexión</div>
              <div class="d-flex align-center ga-2">
                <v-icon
                  :color="isWebsocketConnected ? 'success' : 'warning'"
                  size="small"
                >
                  {{ isWebsocketConnected ? 'mdi-wifi' : 'mdi-wifi-off' }}
                </v-icon>
                <span class="text-body-2">
                  {{ isWebsocketConnected ? 'Tiempo real activo' : 'Solo polling' }}
                </span>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            variant="text"
            @click="showSessionInfo = false"
          >
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'

const store = useStore()

// Reactive data
const showStartDialog = ref(false)
const showCompleteDialog = ref(false)
const showSessionInfo = ref(false)

// Computed properties
const sessionStatus = computed(() => store.getters['session/getSessionStatus'])
const sessionId = computed(() => store.getters['session/getSessionId'])
const pinCode = computed(() => store.getters['session/getPinCode'])
const waitingCount = computed(() => store.getters['session/getWaitingCount'])
const participants = computed(() => store.getters['session/getParticipants'])
const loading = computed(() => store.getters['session/isLoading'])
const canStartQuiz = computed(() => store.getters['session/canStartQuiz'])
const isWebsocketConnected = computed(() => store.getters['session/isWebsocketConnected'])

const statusAlert = computed(() => {
  switch (sessionStatus.value) {
    case 'waiting':
      return {
        type: 'warning',
        color: 'orange',
        icon: 'mdi-clock-time-four-outline',
        status: 'En Espera',
        message: `${waitingCount.value} estudiante(s) esperando. ${canStartQuiz.value ? 'Listo para iniciar.' : 'Esperando más estudiantes.'}`
      }
    case 'active':
      return {
        type: 'success',
        color: 'success',
        icon: 'mdi-play',
        status: 'Activo',
        message: 'El quiz está activo. Los estudiantes pueden responder.'
      }
    case 'completed':
      return {
        type: 'info',
        color: 'info',
        icon: 'mdi-check-circle',
        status: 'Completado',
        message: 'La sesión ha sido completada exitosamente.'
      }
    default:
      return {
        type: 'info',
        color: 'grey',
        icon: 'mdi-help-circle',
        status: 'Desconocido',
        message: 'Estado de sesión no reconocido.'
      }
  }
})

// Emits
const emit = defineEmits(['session-started', 'session-completed', 'quiz-started', 'refresh-requested'])

// Methods
const confirmStartSession = () => {
  showStartDialog.value = true
}

const confirmCompleteSession = () => {
  showCompleteDialog.value = true
}

const startSession = async () => {
  const result = await store.dispatch('session/startSession')

  if (result.success) {
    showStartDialog.value = false
    emit('session-started')
    emit('quiz-started')
  }
}

const completeSession = async () => {
  const result = await store.dispatch('session/completeSession')
  
  if (result.success) {
    showCompleteDialog.value = false
    emit('session-completed')
  }
}

const refreshSession = () => {
  // Emit refresh event to parent
  emit('refresh-requested')
}
</script>

<style scoped>
.font-monospace {
  font-family: 'Courier New', monospace;
}

.v-btn {
  transition: all 0.3s ease;
}

.v-btn:hover {
  transform: translateY(-1px);
}

@media (max-width: 600px) {
  .d-flex.ga-2 {
    flex-direction: column;
    gap: 8px;
  }
  
  .d-flex.ga-2 .v-btn {
    width: 100%;
  }
}
</style>